import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps } from "vue"
import _imports_0 from '/more/discover_tasks/timeline.png'


const _hoisted_1 = { class: "flex justify-center items-center" }
const _hoisted_2 = { class: "mt-20" }
const _hoisted_3 = { class: "mt-20" }
const _hoisted_4 = { class: "mt-20" }
const _hoisted_5 = { class: "flex flex-col md:flex-row gap-8 w-full mt-8" }

import { Motion } from "motion-v";


export default /*@__PURE__*/_defineComponent({
  __name: 'team-collaboration',
  setup(__props) {

const colorMod = useColorMode();

defineOgImage("MainOg", {
  title: "Tasks & Collaboration - Task Management & Compliance",
  description:
    "Streamline team collaboration with smart task management, safety compliance tracking, and automated reporting. Perfect for care homes and residential facilities.",
  theme: "#bc9fff",
  colorMode: "light",
});

useSeoMeta({
  title: "Team Collaboration - Task Management & Compliance | FamilyAxis",
  description:
    "Streamline team collaboration with smart task management, safety compliance tracking, and automated reporting. Assign tasks, share updates, and work together seamlessly in your care home.",
  keywords:
    "family assessment, family assessment centres, team collaboration, task management, care home tasks, compliance tracking, safety checks, automated reminders, staff coordination",
  author: "FamilyAxis",
  ogTitle: "Team Collaboration - Task Management & Compliance | FamilyAxis",
  ogDescription:
    "Streamline team collaboration with smart task management, safety compliance tracking, and automated reporting. Perfect for care homes and residential facilities.",
  ogUrl: "https://familyaxis.com/more/team-collaboration",
  ogType: "website",
  ogSiteName: "FamilyAxis",
  twitterCard: "summary_large_image",
  twitterTitle: "Team Collaboration - Task Management & Compliance | FamilyAxis",
  twitterDescription:
    "Streamline team collaboration with smart task management, safety compliance tracking, and automated reporting.",
  twitterSite: "@familyaxis",
  robots: "index, follow",
});

useHead({
  link: [
    {
      rel: "canonical",
      href: "https://familyaxis.com/more/team-collaboration",
    },
  ],
});

const taskManagementCards = ref([
  {
    title: "Flexible Task Creation",
    description:
      "Quickly build tasks for cleaning, maintenance, health checks, and more. Assign to individuals or teams, set due dates, and mark priority levels.",
    icon: "i-heroicons-plus-circle",
  },
  {
    title: "Template Library",
    description:
      "Hit the ground running with a library of ready-made templates. Customise them or easily design your own from scratch.",
    icon: "i-heroicons-document-duplicate",
    variant: "soft" as const,
  },
  {
    title: "Writing Assistant",
    description:
      "Enhance your communication with integrated tools that help check grammar, summarise lengthy records, and reformat text for clarity. You remain in full control, accepting or rejecting suggestions.",
    icon: "i-heroicons-sparkles",
    variant: "soft" as const,
    spotlight: true,
    class: "lg:row-span-2",
    orientation: "vertical" as const,
    lottie: {
      path: "/animations/ai_chip.json",
      width: 300,
      height: 300,
    },
  },

  {
    title: "Clear Tracking & Completion",
    description:
      "See the status of every task at a glance. Team members can mark tasks complete with a single click, providing instant visual confirmation.",
    icon: "i-heroicons-eye",
    variant: "soft" as const,
  },
  {
    title: "Advanced Text Editor",
    description:
      "Use our powerful built-in editor to format instructions, create lists, and highlight critical information with callout boxes.",
    icon: "i-heroicons-pencil-square",
  },
]);

const viewTypeCards = ref([
  {
    title: "List View",
    description:
      'Your detailed management hub. Organise tasks into custom sections (e.g., "Cleaning & Hygiene," "First Aid"), enjoy full assignment control, and access advanced editing features.',
    icon: "i-heroicons-list-bullet",
    variant: "soft" as const,
  },
  {
    title: "Timeline View",
    description:
      "Perfect for planning. See your tasks plotted on a visual timeline (day, week, month) alongside a full task list for easy rescheduling.",
    icon: "i-heroicons-clock",
    variant: "soft" as const,
  },
  {
    title: "Calendar View",
    description:
      "A clean, high-level overview. Get a sense of the week or month ahead with tasks colour-coded by priority, making busy periods instantly recognisable.",
    icon: "i-heroicons-calendar-days",
    variant: "soft" as const,
  },
]);

const featureCards = ref([
  {
    title: "Safety & Compliance",
    description:
      "Log and monitor essential safety checks, including fire drills, risk assessments, and CCTV compliance with automated reminders.",
    icon: "i-heroicons-shield-check",
  },
  {
    title: "Team Assignment",
    description:
      "Assign tasks to individuals or teams with clear ownership and accountability. Set due dates and priority levels for optimal workflow management.",
    icon: "i-heroicons-user-group",
    variant: "soft" as const,
  },
  {
    title: "Progress Tracking",
    description:
      "Monitor task completion in real-time with visual progress indicators and comprehensive reporting for managers and regulatory compliance.",
    icon: "i-heroicons-chart-bar-square",
  },
  {
    title: "Automated Reminders",
    description:
      "Never miss important deadlines with intelligent systems that notify team members of upcoming tasks and overdue items.",
    icon: "i-heroicons-bell",
    variant: "soft" as const,
  },
  {
    title: "Audit Trail",
    description:
      "Maintain complete records of all task activities for inspections and regulatory requirements with detailed audit trails and reporting.",
    icon: "i-heroicons-document-text",
  },
  {
    title: "Customisable Workflows",
    description:
      "Adapt the system to your organisation's specific processes and requirements with flexible configuration options and custom templates.",
    icon: "i-heroicons-cog-6-tooth",
    variant: "soft" as const,
  },
]);

return (_ctx: any,_cache: any) => {
  const _component_UPageHeader = _resolveComponent("UPageHeader")!
  const _component_UPageSection = _resolveComponent("UPageSection")!
  const _component_LottieWrapper = _resolveComponent("LottieWrapper")!
  const _component_UPageCard = _resolveComponent("UPageCard")!
  const _component_UPageGrid = _resolveComponent("UPageGrid")!
  const _component_UPageHero = _resolveComponent("UPageHero")!
  const _component_LazyShowCaseCard = _resolveComponent("LazyShowCaseCard")!
  const _component_LazyStartSignup = _resolveComponent("LazyStartSignup")!
  const _component_UPageBody = _resolveComponent("UPageBody")!
  const _component_UPage = _resolveComponent("UPage")!
  const _component_UContainer = _resolveComponent("UContainer")!

  return (_openBlock(), _createBlock(_component_UContainer, null, {
    default: _withCtx(() => [
      _createVNode(_component_UPage, { class: "pt-10" }, {
        default: _withCtx(() => [
          _createVNode(_component_UPageHeader, { description: "Assign, track, and collaborate on daily tasks. From safety checks to cleaning rosters, ensure nothing is missed with our smart task management system." }),
          _createVNode(_component_UPageBody, null, {
            default: _withCtx(() => [
              _createVNode(_unref(Motion), {
                as: "div",
                initial: { opacity: 0, y: 20 },
                "while-in-view": { opacity: 1, y: 0 },
                viewport: { once: true },
                transition: { duration: 0.4, delay: 0.2 },
                class: "mt-20"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_UPageSection, {
                    title: "Task Management, Simplified",
                    orientation: "vertical",
                    description: "Create and assign daily tasks easily with our intuitive system that gives you the freedom to build a workflow that perfectly fits your centre's unique needs.",
                    ui: {
              container: 'flex flex-col lg:grid py-16 sm:py-24 lg:py-5 gap-8 sm:gap-16',
            }
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_UPageGrid, null, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(taskManagementCards), (card, index) => {
                    return (_openBlock(), _createBlock(_component_UPageCard, _mergeProps({ key: index }, { ref_for: true }, card, {
                      "data-aos": `fade-up`,
                      "data-aos-delay": (index + 1) * 100,
                      spotlight: card.spotlight ?? false
                    }), {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                          (card.lottie)
                            ? (_openBlock(), _createBlock(_component_LottieWrapper, {
                                key: 0,
                                "animation-link": `/animations/tasks_${_unref(colorMod).value === 'dark' ? 'dark' : 'light'}.json`,
                                "no-margin": true,
                                loop: true,
                                speed: 1.0,
                                scale: 1.0
                              }, null, 8, ["animation-link"]))
                            : _createCommentVNode("", true)
                        ])
                      ]),
                      _: 2
                    }, 1040, ["data-aos-delay", "spotlight"]))
                  }), 128))
                ]),
                _: 1
              }),
              _createVNode(_unref(Motion), {
                as: "div",
                initial: { opacity: 0, y: 20 },
                "while-in-view": { opacity: 1, y: 0 },
                viewport: { once: true },
                transition: { duration: 0.4, delay: 0.25 },
                class: "mt-20"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_UPageSection, {
                    title: "Visualise Your Workflow Your Way",
                    orientation: "horizontal",
                    description: "Every team works differently. Switch between powerful views to manage tasks in the way that suits you best.",
                    align: "right",
                    ui: {
              container: 'flex flex-col lg:grid py-16 sm:py-24 lg:py-5 gap-8 sm:gap-16',
            }
                  }, {
                    default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                      _createElementVNode("div", { class: "relative" }, [
                        _createElementVNode("video", {
                          src: "https://assets.familyaxis.com/taskviews.mp4",
                          autoplay: "",
                          muted: "",
                          loop: "",
                          playsinline: "",
                          class: "w-full h-auto rounded-lg"
                        })
                      ], -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_component_UPageHero, {
                  title: "Choose Your Perfect View",
                  description: "Three ways to manage tasks, each designed for different working styles and planning needs.",
                  ui: {
              container: 'py-8 sm:py-6 lg:py-6',
              title: 'text-4xl sm:text-4xl text-pretty tracking-tight font-bold',
            }
                }),
                _createVNode(_component_UPageGrid, { class: "mt-12" }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(viewTypeCards), (card, index) => {
                      return (_openBlock(), _createBlock(_unref(Motion), {
                        key: index,
                        as: "div",
                        initial: { opacity: 0, y: 20 },
                        "while-in-view": { opacity: 1, y: 0 },
                        viewport: { once: true },
                        transition: { duration: 0.4, delay: (index + 1) * 0.1 }
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_UPageCard, _mergeProps({ ref_for: true }, card), null, 16)
                        ]),
                        _: 2
                      }, 1032, ["transition"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _createVNode(_unref(Motion), {
                as: "div",
                initial: { opacity: 0, y: 20 },
                "while-in-view": { opacity: 1, y: 0 },
                viewport: { once: true },
                transition: { duration: 0.4, delay: 0.3 },
                class: "mt-20"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_UPageSection, {
                    title: "Timeline Planning Made Simple",
                    orientation: "horizontal",
                    description: "Visualise your team's workload with our intuitive timeline view that makes scheduling and rescheduling tasks effortless.",
                    align: "left",
                    ui: {
              container: 'flex flex-col lg:grid py-16 sm:py-24 lg:py-5 gap-8 sm:gap-16',
            }
                  }, {
                    default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                      _createElementVNode("div", { class: "relative" }, [
                        _createElementVNode("img", {
                          src: _imports_0,
                          alt: "Timeline View for Task Planning",
                          class: "w-full h-auto rounded-lg",
                          loading: "lazy"
                        })
                      ], -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_component_UPageHero, {
                  title: "Task Management Features",
                  description: "Everything you need to streamline team collaboration, ensure compliance, and run your centre efficiently.",
                  ui: {
              container: 'py-8 sm:py-6 lg:py-6',
              title: 'text-4xl sm:text-4xl text-pretty tracking-tight font-bold',
            }
                }),
                _createVNode(_component_UPageGrid, { class: "mt-12" }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(featureCards), (card, index) => {
                      return (_openBlock(), _createBlock(_unref(Motion), {
                        key: index,
                        as: "div",
                        initial: { opacity: 0, y: 20 },
                        "while-in-view": { opacity: 1, y: 0 },
                        viewport: { once: true },
                        transition: { duration: 0.4, delay: (index + 1) * 0.1 }
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_UPageCard, _mergeProps({ ref_for: true }, card), null, 16)
                        ]),
                        _: 2
                      }, 1032, ["transition"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_UPageHero, {
                  title: "Continue Your Journey",
                  description: "See how Tasks & Collaboration links with other tools to support centre management.",
                  ui: {
              container: 'py-8 sm:py-6 lg:py-6',
              title: 'text-4xl sm:text-4xl text-pretty tracking-tight font-bold',
            }
                }),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_component_LazyShowCaseCard, {
                    title: "Centralised Dashboard",
                    description: "Manage all your operations from a single dashboard with comprehensive oversight and streamlined workflows.",
                    icon: "i-heroicons-computer-desktop",
                    image: "/images/dash_preview.webp",
                    "image-position": "bottom",
                    link: "/more/centralised-dashboard",
                    class: "w-full md:w-4/7 h-110"
                  }),
                  _createVNode(_component_LazyShowCaseCard, {
                    title: "Family Engagement",
                    description: "Keep families involved in their journey with accessible progress updates and collaborative goal-setting tools.",
                    icon: "i-heroicons-heart",
                    image: "/images/about/care1.jpg",
                    "image-position": "cover",
                    link: "/more/family-engagement",
                    class: "w-full md:w-3/7 h-110"
                  })
                ])
              ]),
              _createVNode(_component_LazyStartSignup)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})